// React Imports
import React, { useEffect, useState } from "react";

// Internationalization Import
import i18n from "i18next";
import { Button, Input, ToggleButton } from "@fluentui/react-components";
import useWindowSize from "../../helpers/useWindowSize";
import { StackShim } from "@fluentui/react-migration-v8-v9";
import {
  Add24Regular,
  Check24Filled,
  Checkmark24Filled,
  Dismiss24Regular,
  Edit24Filled,
  Edit24Regular,
  ShareAndroidRegular,
} from "@fluentui/react-icons";
import { fetchWrapper } from "../../helpers/fetchwrapper";
import { baseUrl } from "../../helpers/constants";
import { IAppUserDto } from "../../Models/IAppUserDto";
import authService from "../api-authorization/AuthorizeService";
import ExecuteDialog from "../Base/ExecuteDialog";

// Internal Imports

// Props
interface IUserProductsCommandBarProps {
  editModeOn: boolean;
  currentTextFilter: string;
  showMakeProductsAvailable: boolean;
  currentUser: IAppUserDto;
  onEditModeChanged(editMode: boolean): void;
  onAddProductClicked(): void;
  onTextFilterChanged(textFilter: string): void;
  onBackendmessage: ({
    message,
    type,
  }: {
    message: string;
    type: "error" | "success";
  }) => void;
  onRefresh: () => void;
}

// Function
function UserProductsCommandBar(props: IUserProductsCommandBarProps) {
  const [textFilter, setTextFilter] = useState<string | undefined>(undefined);
  const [showFreigabeAllDialog, setShowFreigabeAllDialog] =
    useState<boolean>(false);
  const windowSize = useWindowSize();
  const isMobile = windowSize.width < 800;

  useEffect(() => {
    // Get saved data from sessionStorage
    let _sessionTextFilter = sessionStorage.getItem(
      "userProductsSessionTextFilter",
    );
    if (_sessionTextFilter) {
      props.onTextFilterChanged(_sessionTextFilter);

      setTextFilter(_sessionTextFilter);
    } else {
      setTextFilter("");
    }
  }, []);

  return (
    <div className="lueb-InnerContentBlockPadding2px">
      <ExecuteDialog
        isDialogOpen={showFreigabeAllDialog}
        title={i18n.t<string>("shareAllUserProducts")}
        subText={""}
        primaryButtonIconName={"ShareAndroidRegular"}
        primaryButtonText={i18n.t<string>("shareAll")}
        executeUrl={`${baseUrl}/api/account/freigabeAlleProdukteUser?userId=${props.currentUser.id}`}
        onDismiss={() => setShowFreigabeAllDialog(false)}
        onExecute={(fetchwrapperResult: any) => {
          props.onBackendmessage({
            message: i18n.t<string>("shareUserProductWithTeamSuccessMessage"),
            type: "success",
          });
          setShowFreigabeAllDialog(false);
          props.onRefresh();
        }}
        onError={() => {
          props.onBackendmessage({
            message: i18n.t<string>("somethingWentWrong"),
            type: "error",
          });
        }}
      />

      <StackShim
        horizontal
        horizontalAlign="space-between"
        verticalAlign="center"
        tokens={{ childrenGap: isMobile ? 15 : 0 }}
      >
        <StackShim horizontal tokens={{ childrenGap: 5 }}>
          <Button
            size={isMobile ? "large" : undefined}
            icon={<Add24Regular />}
            appearance="subtle"
            onClick={() => {
              props.onAddProductClicked();
            }}
          >
            {isMobile ? undefined : i18n.t<string>("addUserProduct")}
          </Button>
          <Button
            disabled={!props.showMakeProductsAvailable}
            appearance="transparent"
            onClick={() => {
              setShowFreigabeAllDialog(true);
            }}
            icon={<ShareAndroidRegular />}
          >
            {i18n.t<string>("shareUserProductWithTeamButton")}
          </Button>
          <ToggleButton
            appearance="subtle"
            icon={props.editModeOn ? <Edit24Filled /> : <Edit24Regular />}
            onClick={() => {
              props.onEditModeChanged(!props.editModeOn);
            }}
            checked={props.editModeOn}
          >
            {isMobile ? undefined : i18n.t<string>("editMode")}
          </ToggleButton>
        </StackShim>

        <div style={{ padding: 2 }}>
          {textFilter != undefined && (
            <Input
              value={textFilter}
              onChange={(ev: any, data: any) => {
                setTextFilter(data.value);
              }}
              onKeyDown={(e) => {
                if (e.key == "Enter" && props.currentTextFilter != textFilter) {
                  props.onTextFilterChanged(textFilter);
                }
              }}
              onBlur={() => {
                if (props.currentTextFilter != textFilter) {
                  props.onTextFilterChanged(textFilter);
                }
              }}
              placeholder="Filter"
              contentAfter={
                props.currentTextFilter == textFilter ? (
                  <Button
                    appearance="transparent"
                    icon={<Dismiss24Regular />}
                    size="small"
                    onClick={() => {
                      // Save data to sessionStorage
                      sessionStorage.setItem(
                        "userProductsSessionTextFilter",
                        "",
                      );

                      props.onTextFilterChanged("");
                      setTextFilter("");
                    }}
                  />
                ) : (
                  <Button
                    appearance="transparent"
                    icon={<Checkmark24Filled />}
                    size="small"
                    onClick={() => {
                      // Save data to sessionStorage
                      sessionStorage.setItem(
                        "userProductsSessionTextFilter",
                        textFilter,
                      );

                      props.onTextFilterChanged(textFilter);
                    }}
                  />
                )
              }
            />
          )}
        </div>
      </StackShim>
    </div>
  );
}

// Export Function
export default UserProductsCommandBar;
